import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config";
import BackButton from "./BackButton";
import {
  Button,
  Container,
  Typography,
  Box,
  Avatar,
  Grid,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  Healing as HealingIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Wc as GenderIcon,
  FamilyRestroom as ParentIcon,
  ExpandMore as ExpandMoreIcon,
  BarChart as BarChartIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

// Helper to determine chip color based on risk
const getInterpretationColor = (interpretation) => {
  switch (interpretation) {
    case "Low Risk":
    case "Age Appropriate / Achieved":
      return "success";
    case "Moderate Risk":
    case "Emerging Skills":
      return "warning";
    case "High Risk":
    case "Significant Delays":
      return "error";
    default:
      return "primary";
  }
};

// Helper to calculate score percentage
const calculatePercentage = (score, max) => (score / max) * 100;

const ChildProfile = () => {
  const { t } = useTranslation();
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTherapiesOpen, setEditTherapiesOpen] = useState(false);
  const [selectedTherapies, setSelectedTherapies] = useState([]);

  const handleOpenEditTherapies = () => {
      let current = [];
      try {
          current = typeof child.recommended_therapies === 'string' ? JSON.parse(child.recommended_therapies) : (child.recommended_therapies || []);
      } catch(e) { current = []; }
      setSelectedTherapies(current);
      setEditTherapiesOpen(true);
  };

  const handleTherapyToggle = (therapy) => {
      setSelectedTherapies(prev => 
          prev.includes(therapy) ? prev.filter(t => t !== therapy) : [...prev, therapy]
      );
  };

  const handleSaveTherapies = async () => {
      try {
          await axios.post(`${API_BASE_URL}/update_therapies.php`, {
              child_id: childId,
              recommended_therapies: selectedTherapies
          });
          setChild({ ...child, recommended_therapies: selectedTherapies });
          setEditTherapiesOpen(false);
      } catch (err) {
          console.error("Failed to update therapies", err);
      }
  };

  useEffect(() => {
    const fetchChildData = async () => {
      setLoading(true);
      try {
        const childrenResponse = await axios.get(
          `${API_BASE_URL}/get_children.php`
        );
        const currentChild = childrenResponse.data.find(
          (c) => c.id == childId
        );
        setChild(currentChild);

        const assessmentsResponse = await axios.get(
          `${API_BASE_URL}/get_assessments.php?child_id=${childId}`
        );
        setAssessments(assessmentsResponse.data);
      } catch (error) {
        console.error("Failed to fetch child data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchChildData();
  }, [childId]);

  const userRole = localStorage.getItem('userRole');
  const therapyPath = userRole === 'behavioral' ? `/therapy/${childId}/1` :
                     userRole === 'occupational' ? `/therapy/${childId}/2` :
                     userRole === 'speech' ? `/therapy/${childId}/3` :
                     `/therapy/${childId}`;

  if (loading) {
    return (
      <Typography sx={{ textAlign: "center", mt: 5 }}>
        {t("loading_text")}
      </Typography>
    );
  }

  if (!child) {
    return (
      <Typography sx={{ textAlign: "center", mt: 5 }}>
        Child not found.
      </Typography>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", py: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg">
        <BackButton />
        {/* Header Section */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 4 },
            mt: { xs: 3, md: 5 },
            mb: { xs: 3, md: 5 },
            borderRadius: "16px",
            background: "linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)",
            border: "1px solid #e0e0e0",
          }}
        >
          <Grid container spacing={4} alignItems="center">
            {/* Avatar and Basic Info */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 3 } }}
            >
              <Avatar
                src={child.photo_url || ""}
                sx={{
                  width: { xs: 70, sm: 100 },
                  height: { xs: 70, sm: 100 },
                  bgcolor: "primary.main",
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                }}
              >
                {child.full_name.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", color: "primary.dark" }}
                >
                  {child.full_name}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: 1,
                    color: "text.secondary",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <CalendarIcon fontSize="small" /> DOB:{" "}
                    {new Date(child.date_of_birth).toLocaleDateString()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <GenderIcon fontSize="small" /> {child.gender}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                    color: "text.secondary",
                  }}
                >
                  <ParentIcon fontSize="small" /> Parent:{" "}
                  {child.parent_name || "N/A"}
                </Typography>
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>Therapies:</Typography>
                  {(() => {
                      let therapies = [];
                      try { therapies = typeof child.recommended_therapies === 'string' ? JSON.parse(child.recommended_therapies) : (child.recommended_therapies || []); } catch(e){}
                      return therapies.length > 0 ? therapies.map(th => <Chip key={th} label={th} size="small" color="primary" variant="outlined" />) : <Typography variant="caption" color="text.disabled">None set</Typography>;
                  })()}
                  <IconButton size="small" onClick={handleOpenEditTherapies} sx={{ ml: 1, bgcolor: 'background.paper', boxShadow: 1 }}><EditIcon fontSize="small" color="action" /></IconButton>
                </Box>
              </Box>
            </Grid>

            {/* Quick Action Buttons */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  component={Link}
                  to={`/assessment/${childId}`}
                  startIcon={<AssessmentIcon />}
                  sx={{
                    borderRadius: "20px",
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  {t("start_new_assessment_button")}
                </Button>
                <Button
                  variant="outlined"
                  component={Link}
                  to={therapyPath}
                  startIcon={<HealingIcon />}
                  sx={{
                    borderRadius: "20px",
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  {t("manage_therapy_button")}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  component={Link}
                  to={`/child-report/${childId}`}
                  startIcon={<DescriptionIcon />}
                  sx={{
                    borderRadius: "20px",
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  {t("generate_report_button")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Assessment History Section */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: "text.primary",
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <BarChartIcon />
            {t("assessment_history")}
          </Typography>
          {assessments.length === 0 ? (
            <Paper sx={{ textAlign: "center", py: 6, borderRadius: "16px" }}>
              <AssessmentIcon
                sx={{ fontSize: 60, color: "text.disabled", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                {t("no_assessments_yet")}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Click 'Start New Assessment' to begin.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {assessments.map((assessment) => {
                const isOT = assessment.assessment_type === "ot_reassessment";
                const typeName = isOT ? "Occupational Therapy Reassessment" : "Autism & ADHD Assessment";

                // Map labels and max scores based on assessment type
                const s1Label = isOT ? "Physical & Postural Assesment" : t("social_communication_score_label");
                const s1Max = isOT ? 15 : 40;
                
                const s2Label = isOT ? "Motor Skills & Transitions" : t("repetitive_behavior_score_label");
                const s2Max = isOT ? 33 : 32;

                const s3Label = isOT ? "Behavior & Attention" : t("inattention_score_label");
                const s3Max = isOT ? 15 : 28;

                const s4Label = isOT ? "Self Care & Daily Living" : t("hyperactivity_score_label");
                const s4Max = isOT ? 15 : 24;

                return (
                  <Accordion
                    key={assessment.id}
                    sx={{
                      borderRadius: "12px",
                      "&:before": { display: "none" },
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: { xs: 1, sm: 2 },
                          width: "100%",
                          pr: 2,
                        }}
                      >
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 500,
                            wordBreak: "break-word",
                            fontSize: { xs: "1rem", sm: "1.25rem" }
                          }}
                        >
                          {typeName} - {new Date(assessment.created_at).toLocaleDateString(
                            "en-US",
                            { year: "numeric", month: "long", day: "numeric" }
                          )}
                        </Typography>
                        <Chip
                          label={assessment.interpretation || "Completed"}
                          color={getInterpretationColor(
                            assessment.interpretation
                          )}
                          size="small"
                          sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ bgcolor: "#fafafa", borderTop: "1px solid #eee" }}
                    >
                      <Grid container spacing={3}>
                        {/* Score Item 1 */}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {s1Label}
                          </Typography>
                          <Tooltip
                            title={`Score: ${assessment.social_communication_score}/${s1Max}`}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={calculatePercentage(
                                assessment.social_communication_score,
                                s1Max
                              )}
                              sx={{ height: 8, borderRadius: 5, mt: 1 }}
                            />
                          </Tooltip>
                        </Grid>
                        {/* Score Item 2 */}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {s2Label}
                          </Typography>
                          <Tooltip
                            title={`Score: ${assessment.repetitive_behavior_score}/${s2Max}`}
                          >
                            <LinearProgress
                              color="success"
                              variant="determinate"
                              value={calculatePercentage(
                                assessment.repetitive_behavior_score,
                                s2Max
                              )}
                              sx={{ height: 8, borderRadius: 5, mt: 1 }}
                            />
                          </Tooltip>
                        </Grid>
                        {/* Score Item 3 */}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {s3Label}
                          </Typography>
                          <Tooltip
                            title={`Score: ${assessment.inattention_score}/${s3Max}`}
                          >
                            <LinearProgress
                              color="warning"
                              variant="determinate"
                              value={calculatePercentage(
                                assessment.inattention_score,
                                s3Max
                              )}
                              sx={{ height: 8, borderRadius: 5, mt: 1 }}
                            />
                          </Tooltip>
                        </Grid>
                        {/* Score Item 4 */}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {s4Label}
                          </Typography>
                          <Tooltip
                            title={`Score: ${assessment.hyperactivity_score}/${s4Max}`}
                          >
                            <LinearProgress
                              color="error"
                              variant="determinate"
                              value={calculatePercentage(
                                assessment.hyperactivity_score,
                                s4Max
                              )}
                              sx={{ height: 8, borderRadius: 5, mt: 1 }}
                            />
                          </Tooltip>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          component={Link}
                          to={`/child-report/${childId}?assessmentId=${assessment.id}`}
                          startIcon={<DescriptionIcon />}
                        >
                          View Full Report
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </Box>
      </Container>

      {/* Edit Therapies Dialog */}
      <Dialog open={editTherapiesOpen} onClose={() => setEditTherapiesOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Recommended Therapies</DialogTitle>
          <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select the therapies required for this child.</Typography>
              <FormGroup>
                  <FormControlLabel 
                      control={<Checkbox checked={selectedTherapies.includes('behavioral')} onChange={() => handleTherapyToggle('behavioral')} />} 
                      label="Behavioral Therapy" 
                  />
                  <FormControlLabel 
                      control={<Checkbox checked={selectedTherapies.includes('occupational')} onChange={() => handleTherapyToggle('occupational')} />} 
                      label="Occupational Therapy" 
                  />
                  <FormControlLabel 
                      control={<Checkbox checked={selectedTherapies.includes('speech')} onChange={() => handleTherapyToggle('speech')} />} 
                      label="Speech Therapy" 
                  />
              </FormGroup>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setEditTherapiesOpen(false)} color="inherit">Cancel</Button>
              <Button onClick={handleSaveTherapies} variant="contained" color="primary" sx={{ borderRadius: 2 }}>Save Changes</Button>
          </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChildProfile;
